import React, { useMemo, useState } from "react";
import { ObjectionActionModal } from "./objectionRequests/ObjectionActionModal";
import { ObjectionDetailModal } from "./objectionRequests/ObjectionDetailModal";
import { ObjectionFilters } from "./objectionRequests/ObjectionFilters";
import { ObjectionRequestsStats } from "./objectionRequests/ObjectionRequestsStats";
import { ObjectionRequestsTable } from "./objectionRequests/ObjectionRequestsTable";
import { useObjectionRequests } from "./objectionRequests/useObjectionRequests";
import type { ActionType, Objection } from "./objectionRequests/types";

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const ObjectionRequestsController: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState(getTodayDate);
  const [selectedObjection, setSelectedObjection] = useState<Objection | null>(
    null,
  );
  const [detailObjection, setDetailObjection] = useState<Objection | null>(
    null,
  );
  const [actionType, setActionType] = useState<ActionType | null>(null);

  const { objections, isLoading, refetch } = useObjectionRequests(selectedDate);

  const filteredObjections = useMemo(
    () =>
      objections.filter((objection) => {
        const query = searchTerm.toLowerCase();
        const matchesSearch =
          objection?.cnr?.toLowerCase()?.includes(query) ||
          objection?.caseTitle?.toLowerCase()?.includes(query) ||
          objection?.objectionTitle?.toLowerCase()?.includes(query) ||
          objection?.firstName?.toLowerCase()?.includes(query) ||
          objection?.courtName?.toLowerCase()?.includes(query);

        const matchesStatus =
          statusFilter === "all" || objection.status === statusFilter;

        return matchesSearch && matchesStatus;
      }),
    [objections, searchTerm, statusFilter],
  );

  const openActionModal = (objection: Objection, type: ActionType) => {
    setSelectedObjection(objection);
    setActionType(type);
  };

  const openActionFromDetails = (objection: Objection, type: ActionType) => {
    openActionModal(objection, type);
  };

  const closeActionModal = () => {
    setSelectedObjection(null);
    setActionType(null);
  };

  return (
    <div className="max-w-screen w-full min-h-screen">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">
            Objection Requests
          </h2>
        </div>

        <ObjectionRequestsStats
          objections={objections}
          activeStatus={statusFilter}
          onStatusSelect={setStatusFilter}
        />

        <ObjectionFilters
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          selectedDate={selectedDate}
          visibleCount={filteredObjections.length}
          totalCount={objections.length}
          onSearchChange={setSearchTerm}
          onStatusChange={setStatusFilter}
          onDateChange={setSelectedDate}
        />

        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading objection requests...</p>
          </div>
        ) : (
          <ObjectionRequestsTable
            objections={filteredObjections}
            onViewDetails={setDetailObjection}
            onAction={openActionModal}
          />
        )}
      </div>

      {detailObjection && (
        <ObjectionDetailModal
          objection={detailObjection}
          onClose={() => setDetailObjection(null)}
          onAction={openActionFromDetails}
        />
      )}

      {selectedObjection && actionType && (
        <ObjectionActionModal
          objection={selectedObjection}
          actionType={actionType}
          onClose={closeActionModal}
          onCompleted={refetch}
        />
      )}
    </div>
  );
};

export default ObjectionRequestsController;
